import type {
  MediaToolsActiveValidate,
  OneOfMinActiveValidateRule,
  MediaToolsModelConfig,
  MediaToolsModelToolConfig,
  ModelToolParameterInputItem,
  FlowNodeData,
} from '$/domains/workFlowV3/type';
import type { FlowNodeEntity } from '@flowgram.ai/free-layout-editor';
import type { LanguageKeyMap } from '$/store/languageStore/smartcatTranslation';
import { LanguageCode, replaceTemplateWithElements, useLanguageStore } from '$/store/languageStore';
import useFlowNextStore from '$/store/useTeamFlowStore';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';
import { buildMcpInfoFromGenerationConfig, getDefaultParams } from './modelConfig';
import { getMcpInfoParameterKey } from '.';
import { isEffectTemplateNodeData } from './isEffectTemplateNode';

type InputType = 'text' | 'image' | 'video' | 'audio';

/** 各类型连入节点的计数 */
export type InputTypeCounts = Record<InputType, number>;

/**
 * 媒体节点类型 → modelConfig 中对应的 key
 */
const MEDIA_NODE_TYPE_TO_CONFIG_KEY: Partial<Record<TVFlowNodeNextTypeEnum, 'image' | 'video' | 'audio'>> = {
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: 'image',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: 'video',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: 'audio',
};

/**
 * 从 inputNodeId 推导出各类型的连入节点数量
 */
function deriveInputTypeCounts(inputNodeId: FlowNodeData['inputNodeId']): InputTypeCounts {
  return {
    text: inputNodeId?.textNodeIds?.length ?? 0,
    image: inputNodeId?.imageNodeIds?.length ?? 0,
    video: inputNodeId?.videoNodeIds?.length ?? 0,
    audio: inputNodeId?.audioNodeIds?.length ?? 0,
  };
}

/** input_parameters 中不参与连入数量校验的 key */
const SKIPPED_INPUT_PARAMETER_KEYS = new Set(['text_list']);

/**
 * 过滤掉无需校验的 input_parameters 条目。
 */
function getValidatedInputParameters(
  inputParams: Record<string, ModelToolParameterInputItem>,
): Array<[string, ModelToolParameterInputItem]> {
  return Object.entries(inputParams).filter(
    ([key]) => !SKIPPED_INPUT_PARAMETER_KEYS.has(key),
  );
}

/**
 * 推导允许连入的媒体类型：需校验项 + 仅跳过 min/max 的项（如 text_list 仍声明可连 text）。
 */
function getAllowedConnectionTypes(
  inputParams: Record<string, ModelToolParameterInputItem>,
): Set<InputType> {
  const allowed = new Set(
    getValidatedInputParameters(inputParams).map(([, param]) => param.type as InputType),
  );
  for (const [key, param] of Object.entries(inputParams)) {
    if (SKIPPED_INPUT_PARAMETER_KEYS.has(key) && param.type) {
      allowed.add(param.type as InputType);
    }
  }
  return allowed;
}

const INPUT_TYPE_KEYS: InputType[] = ['text', 'image', 'video', 'audio'];

/**
 * 是否存在 tool 未声明类型的连入（如文生视频仅 prompt、却连了图片节点）。
 */
function hasUndeclaredConnectionTypes(
  counts: InputTypeCounts,
  allowedTypes: Set<InputType>,
): boolean {
  return INPUT_TYPE_KEYS.some(
    (type) => (counts[type] ?? 0) > 0 && !allowedTypes.has(type),
  );
}

/**
 * 读取 active_validate oneof + min 规则下需成组校验的类型集合。
 */
function getOneOfMinValidateTypes(
  activeValidate: MediaToolsActiveValidate | undefined,
): Set<InputType> | null {
  if (!isOneOfMinActiveValidate(activeValidate)) {
    return null;
  }
  return new Set(activeValidate.options as InputType[]);
}

/**
 * 判断是否为 oneof + min 规则。
 */
function isOneOfMinActiveValidate(
  activeValidate: MediaToolsActiveValidate | undefined,
): activeValidate is OneOfMinActiveValidateRule {
  return activeValidate?.type === 'oneof' && activeValidate.validate_str === 'min';
}

/**
 * 汇总某连入类型在 input_parameters 中的最大 min 要求（跳过 text_list 等条目）。
 */
function getMinRequirementForType(
  inputParams: Record<string, ModelToolParameterInputItem>,
  type: InputType,
): number {
  return getValidatedInputParameters(inputParams)
    .filter(([, param]) => param.type === type)
    .reduce((maxMin, [, param]) => Math.max(maxMin, param.min ?? 0), 0);
}

/**
 * oneof + min：options 内至少一种类型满足 min；配置 min 为 0 时仍要求该类型至少连入 1 个。
 */
function passesOneOfMinValidate(
  activeValidate: OneOfMinActiveValidateRule,
  counts: InputTypeCounts,
  inputParams: Record<string, ModelToolParameterInputItem>,
): boolean {
  return activeValidate.options.some((type) => {
    const inputType = type as InputType;
    const minReq = getMinRequirementForType(inputParams, inputType);
    const count = counts[inputType] ?? 0;
    const effectiveMin = minReq > 0 ? minReq : 1;
    return count >= effectiveMin;
  });
}

/**
 * 检查 tool 是否与给定的 inputTypeCounts 兼容：
 * 1. 有连入时，每种连入类型必须在需校验的 input_parameters 中声明；多连未声明类型则不可用
 * 2. 仅含 text_list 等跳过项时，不允许任何连入（文案走节点内 prompt）
 * 3. 各 input_parameter（text_list 除外）连入数量须在 [min, max] 内；active_validate oneof 时分组 OR 校验 min
 * 4. 无连入时，仅当所有需校验项 min 均为 0 才兼容（有 active_validate oneof 时至少需一种媒体连入）
 */
export function isToolCompatible(
  tool: MediaToolsModelToolConfig,
  counts: InputTypeCounts,
): boolean {
  const inputParams = tool.input_parameters;
  if (!inputParams) return false;

  const validatedParams = getValidatedInputParameters(inputParams);
  const allowedTypes = getAllowedConnectionTypes(inputParams);
  const totalInput = counts.text + counts.image + counts.video + counts.audio;
  const oneOfMinTypes = getOneOfMinValidateTypes(tool.active_validate);

  if (totalInput > 0 && hasUndeclaredConnectionTypes(counts, allowedTypes)) {
    return false;
  }

  if (!validatedParams.length) {
    return totalInput === 0 || !hasUndeclaredConnectionTypes(counts, allowedTypes);
  }

  const allInputsOptional = validatedParams.every(
    ([, param]) => (param.min ?? 0) === 0,
  );

  if (totalInput === 0) {
    if (isOneOfMinActiveValidate(tool.active_validate)
      && !passesOneOfMinValidate(tool.active_validate, counts, inputParams)) {
      return false;
    }
    if (!allInputsOptional && !oneOfMinTypes) {
      return false;
    }
  }

  for (const [, param] of validatedParams) {
    const paramType = param.type as InputType;
    const count = counts[paramType] ?? 0;
    const min = param.min ?? 0;
    const max = param.max ?? Infinity;

    if (!oneOfMinTypes?.has(paramType) && count < min) {
      return false;
    }
    if (count > max) return false;
  }

  if (isOneOfMinActiveValidate(tool.active_validate)
    && !passesOneOfMinValidate(tool.active_validate, counts, inputParams)) {
    return false;
  }

  return true;
}

/**
 * 根据当前连入节点，计算 tool_list 中不可手动选择的 node_model_id 集合。
 */
export function getDisabledToolIds(
  tools: MediaToolsModelToolConfig[],
  inputNodeId: FlowNodeData['inputNodeId'],
): Set<string> {
  const counts = deriveInputTypeCounts(inputNodeId);
  return new Set(
    tools
      .filter((tool) => !isToolCompatible(tool, counts))
      .map((tool) => tool.node_model_id),
  );
}

/**
 * 读取无占位符的翻译文案。
 */
function getSmartcatTranslationText<K extends keyof LanguageKeyMap>(key: K): string {
  return useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v[key]);
}

type InputTypeTranslationKey = 'text_node' | 'image_node' | 'video_node' | 'audio_node';

const INPUT_TYPE_TRANSLATION_KEYS: Record<InputType, InputTypeTranslationKey> = {
  text: 'text_node',
  image: 'image_node',
  video: 'video_node',
  audio: 'audio_node',
};

/**
 * 读取连入节点类型的翻译文案。
 */
function getInputTypeLabel(type: InputType): string {
  return getSmartcatTranslationText(INPUT_TYPE_TRANSLATION_KEYS[type]);
}

/**
 * 返回素材类型列表的连接分隔符（中文顿号，其他语言逗号）。
 */
function getMediaTypeListSeparator(): string {
  const { languageCode } = useLanguageStore.getState().languageStore;
  if (
    languageCode === LanguageCode.zhHans
    || languageCode === LanguageCode.zh
    || languageCode === LanguageCode.zhHant
  ) {
    return '、';
  }
  return ', ';
}

/**
 * 汇总指定类型在 input_parameters 中的 max（未配置 max 时按 1 计）。
 */
function sumMaxForConnectionTypes(
  inputParams: Record<string, ModelToolParameterInputItem>,
  types: InputType[],
): number {
  return types.reduce((sum, type) => {
    const typeMax = getValidatedInputParameters(inputParams)
      .filter(([, param]) => param.type === type)
      .reduce((maxValue, [, param]) => Math.max(maxValue, param.max ?? 0), 0);
    return sum + (typeMax > 0 ? typeMax : 1);
  }, 0);
}

/**
 * 读取某类型在 input_parameters 中的 max 上限（无配置时为 Infinity）。
 */
function getMaxRequirementForType(
  inputParams: Record<string, ModelToolParameterInputItem>,
  type: InputType,
): number {
  const maxValues = getValidatedInputParameters(inputParams)
    .filter(([, param]) => param.type === type)
    .map(([, param]) => param.max ?? Infinity);
  if (!maxValues.length) return Infinity;
  return Math.min(...maxValues);
}

type ConnectionRequirementReasonKey =
  | 'need_image_node'
  | 'need_image_node_range'
  | 'need_multiple_node_range';

/**
 * 读取连入节点数量要求提示文案，并替换 {min}/{max}/{sum_max} 占位符。
 */
function getConnectionRequirementMessage(
  reasonKey: ConnectionRequirementReasonKey,
  params: Record<string, string>,
): string {
  return replaceTemplateWithElements(reasonKey, params) as string;
}

/**
 * 拼接连入节点数量要求提示（规则 1、2）。
 */
function buildRequiresConnectionMessage(
  min: number,
  max: number,
): string {
  const effectiveMax = max === Infinity ? min : max;
  if (effectiveMax === min) {
    return getConnectionRequirementMessage('need_image_node', {
      min: String(min),
    });
  }
  return getConnectionRequirementMessage('need_image_node_range', {
    min: String(min),
    max: String(effectiveMax),
  });
}

/**
 * 推导连入数量与 min/max 不匹配时的提示（规则 1、2）。
 */
function getMediaCountRequirementReason(
  tool: MediaToolsModelToolConfig,
  counts: InputTypeCounts,
): string | undefined {
  const inputParams = tool.input_parameters ?? {};
  const oneOfMinTypes = getOneOfMinValidateTypes(tool.active_validate);
  const declaredTypes = [...new Set(
    getValidatedInputParameters(inputParams)
      .map(([, param]) => param.type as InputType)
      .filter((type) => type !== 'text'),
  )];

  const involvedTypes: InputType[] = [];
  let displayMin = Infinity;
  let displayMax = 0;
  let hasMismatch = false;

  for (const type of declaredTypes) {
    const min = getMinRequirementForType(inputParams, type);
    const max = getMaxRequirementForType(inputParams, type);
    const count = counts[type] ?? 0;
    const minApplies = !oneOfMinTypes?.has(type);
    const belowMin = minApplies && count < min;
    const aboveMax = count > max;

    if (!belowMin && !aboveMax) continue;

    hasMismatch = true;
    involvedTypes.push(type);
    if (minApplies) {
      displayMin = Math.min(displayMin, min > 0 ? min : 1);
    }
    if (max !== Infinity) {
      displayMax = Math.max(displayMax, max);
    } else if (minApplies && min > 0) {
      displayMax = Math.max(displayMax, min);
    }
  }

  if (!hasMismatch) return undefined;

  const resolvedMin = displayMin === Infinity ? 1 : displayMin;
  const resolvedMax = displayMax > 0 ? displayMax : resolvedMin;

  return buildRequiresConnectionMessage(resolvedMin, resolvedMax);
}

/**
 * 推导 active_validate oneof + min 不满足时的提示（规则 3）。
 */
function getActiveValidateRequirementReason(
  tool: MediaToolsModelToolConfig,
  counts: InputTypeCounts,
): string | undefined {
  if (!isOneOfMinActiveValidate(tool.active_validate)) return undefined;

  const inputParams = tool.input_parameters ?? {};
  if (passesOneOfMinValidate(tool.active_validate, counts, inputParams)) {
    return undefined;
  }

  const options = tool.active_validate.options as InputType[];
  const maxSum = sumMaxForConnectionTypes(inputParams, options);

  return getConnectionRequirementMessage('need_multiple_node_range', {
    sum_max: String(maxSum),
  });
}

/**
 * 无连入时，推导工具所需媒体连入数量提示（规则 1、2 兜底）。
 */
function getEmptyInputRequirementReason(
  tool: MediaToolsModelToolConfig,
): string | undefined {
  const inputParams = tool.input_parameters ?? {};
  const oneOfMinTypes = getOneOfMinValidateTypes(tool.active_validate);
  const declaredTypes = [...new Set(
    getValidatedInputParameters(inputParams)
      .map(([, param]) => param.type as InputType)
      .filter((type) => type !== 'text'),
  )];

  const requiredTypes: InputType[] = [];
  let displayMin = 0;
  let displayMax = 0;

  for (const type of declaredTypes) {
    if (oneOfMinTypes?.has(type)) continue;

    const min = getMinRequirementForType(inputParams, type);
    const max = getMaxRequirementForType(inputParams, type);
    if (min === 0) continue;

    requiredTypes.push(type);
    displayMin = Math.max(displayMin, min);
    if (max !== Infinity) {
      displayMax = Math.max(displayMax, max);
    } else {
      displayMax = Math.max(displayMax, min);
    }
  }

  if (!requiredTypes.length) return undefined;

  return buildRequiresConnectionMessage(displayMin, displayMax || displayMin);
}

/**
 * 推导单个禁用 tool 的 hover 提示文案；兼容时返回 undefined。
 */
export function getDisabledToolReason(
  tool: MediaToolsModelToolConfig,
  inputNodeId: FlowNodeData['inputNodeId'],
): string | undefined {
  const counts = deriveInputTypeCounts(inputNodeId);
  if (isToolCompatible(tool, counts)) return undefined;

  const inputParams = tool.input_parameters ?? {};
  const allowedTypes = getAllowedConnectionTypes(inputParams);
  const totalInput = counts.text + counts.image + counts.video + counts.audio;

  if (totalInput > 0 && hasUndeclaredConnectionTypes(counts, allowedTypes)) {
    return getSmartcatTranslationText('media_connected_does_not_support_text');
  }

  const activeValidateReason = getActiveValidateRequirementReason(tool, counts);
  if (activeValidateReason) return activeValidateReason;

  const countMismatchReason = getMediaCountRequirementReason(tool, counts);
  if (countMismatchReason) return countMismatchReason;

  if (totalInput === 0) {
    return getEmptyInputRequirementReason(tool);
  }

  return undefined;
}

/**
 * 批量推导禁用 tool 的 hover 提示文案。
 */
export function getDisabledToolReasons(
  tools: MediaToolsModelToolConfig[],
  inputNodeId: FlowNodeData['inputNodeId'],
): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const tool of tools) {
    const reason = getDisabledToolReason(tool, inputNodeId);
    if (reason) {
      reasons.set(tool.node_model_id, reason);
    }
  }
  return reasons;
}

/**
 * 检查 model 下是否至少有一个 tool 与当前连入兼容。
 */
export function isModelCompatible(
  model: MediaToolsModelConfig,
  inputNodeId: FlowNodeData['inputNodeId'],
): boolean {
  const counts = deriveInputTypeCounts(inputNodeId);
  return Boolean(findMatchingToolInModel(model, counts));
}

/**
 * 根据当前连入节点，计算不可选择的 model_id（其下所有 tool 均不兼容）。
 */
export function getDisabledModelIds(
  models: MediaToolsModelConfig[],
  inputNodeId: FlowNodeData['inputNodeId'],
): Set<string> {
  return new Set(
    models
      .filter((model) => !isModelCompatible(model, inputNodeId))
      .map((model) => model.model_id),
  );
}

type DisabledModelReasonKey =
  | 'do_not_support_image'
  | 'need_image_and_video';

/**
 * 将素材类型列表格式化为翻译后的「图片、视频」类文案（{media} 占位符替换用）。
 */
function formatMediaTypeLabels(types: InputType[]): string {
  return [...new Set(types)]
    .map((type) => getInputTypeLabel(type))
    .join(getMediaTypeListSeparator());
}

/**
 * 读取禁用模型 hover 提示文案，并替换 {media} 占位符。
 */
function getDisabledModelReasonMessage(
  reasonKey: DisabledModelReasonKey,
  mediaTypes?: InputType[],
): string {
  return replaceTemplateWithElements(reasonKey, {
    media: mediaTypes?.length ? formatMediaTypeLabels(mediaTypes) : '',
  }) as string;
}

/**
 * 判断当前是否无任何连入节点。
 */
function hasNoConnectedInput(counts: InputTypeCounts): boolean {
  return counts.text + counts.image + counts.video + counts.audio === 0;
}

/**
 * 判断当前连入是否为纯文字（仅有 text，无图片/视频/音频）。
 */
function isTextOnlyInput(counts: InputTypeCounts): boolean {
  return counts.text > 0
    && counts.image === 0
    && counts.video === 0
    && counts.audio === 0;
}

/**
 * 判断是否应展示「不支持纯文字生成」禁用文案（无连入或仅连入文字）。
 */
function shouldShowTextOnlyDisabledReason(counts: InputTypeCounts): boolean {
  return hasNoConnectedInput(counts) || isTextOnlyInput(counts);
}

/**
 * 判断 tool 是否要求同时连入图片与视频（二者 min 均须满足，且非 oneof OR 语义）。
 */
function toolRequiresImageAndVideo(tool: MediaToolsModelToolConfig): boolean {
  const inputParams = tool.input_parameters ?? {};
  const validatedParams = getValidatedInputParameters(inputParams);
  const oneOfMinTypes = getOneOfMinValidateTypes(tool.active_validate);
  const imageParam = validatedParams.find(([, param]) => param.type === 'image');
  const videoParam = validatedParams.find(([, param]) => param.type === 'video');

  if (!imageParam || !videoParam) return false;

  const imageMin = imageParam[1].min ?? 0;
  const videoMin = videoParam[1].min ?? 0;
  const imageRequired = !oneOfMinTypes?.has('image') && imageMin >= 1;
  const videoRequired = !oneOfMinTypes?.has('video') && videoMin >= 1;

  return imageRequired && videoRequired;
}

/**
 * 判断 model 下是否存在要求同时连入图片与视频的 tool。
 */
function modelRequiresImageAndVideo(model: MediaToolsModelConfig): boolean {
  return model.tool_list?.some(toolRequiresImageAndVideo) ?? false;
}

/**
 * 判断是否为「图片、视频仅连入其一」且模型要求同时连入二者（规则 3）。
 */
function shouldShowRequiresImageAndVideoRule(
  model: MediaToolsModelConfig,
  counts: InputTypeCounts,
): boolean {
  if (!modelRequiresImageAndVideo(model)) return false;

  const hasImage = counts.image > 0;
  const hasVideo = counts.video > 0;

  return hasImage !== hasVideo;
}

/**
 * 汇总已连入但 model 下所有 tool 均不支持的素材类型。
 */
function getUnsupportedConnectedMediaTypes(
  tools: MediaToolsModelToolConfig[],
  counts: InputTypeCounts,
): InputType[] {
  if (!tools.length) return [];

  return INPUT_TYPE_KEYS.filter(
    (type) => (counts[type] ?? 0) > 0 && allToolsRejectMediaType(tools, type),
  );
}

/**
 * 汇总已连入数量超出所有声明该类型 tool 的 max 上限的素材类型。
 */
function getExcessiveConnectedMediaTypes(
  tools: MediaToolsModelToolConfig[],
  counts: InputTypeCounts,
): InputType[] {
  return INPUT_TYPE_KEYS.filter((type) => {
    const count = counts[type] ?? 0;
    if (count === 0) return false;

    const toolsDeclaringType = tools.filter((tool) => {
      const allowedTypes = getAllowedConnectionTypes(tool.input_parameters ?? {});
      return allowedTypes.has(type);
    });
    if (!toolsDeclaringType.length) return false;

    return toolsDeclaringType.every((tool) => {
      const max = getMaxRequirementForType(tool.input_parameters ?? {}, type);
      return count > max;
    });
  });
}

/**
 * 无连入时，推导 model 下首个 tool 所需的素材类型。
 */
function getEmptyInputRequiredTypes(
  tools: MediaToolsModelToolConfig[],
): InputType[] {
  for (const tool of tools) {
    const inputParams = tool.input_parameters ?? {};
    const oneOfMinTypes = getOneOfMinValidateTypes(tool.active_validate);

    if (isOneOfMinActiveValidate(tool.active_validate)) {
      return tool.active_validate.options as InputType[];
    }

    const requiredTypes = getValidatedInputParameters(inputParams)
      .filter(([, param]) => {
        const type = param.type as InputType;
        return !oneOfMinTypes?.has(type) && (param.min ?? 0) > 0;
      })
      .map(([, param]) => param.type as InputType);

    if (requiredTypes.length) {
      return [...new Set(requiredTypes)];
    }
  }
  return [];
}

/**
 * 汇总因 min 未满足或 oneof + min 未满足而缺少的素材类型。
 */
function getInsufficientConnectedMediaTypes(
  tools: MediaToolsModelToolConfig[],
  counts: InputTypeCounts,
): InputType[] {
  const missingTypes = new Set<InputType>();

  for (const tool of tools) {
    const inputParams = tool.input_parameters ?? {};
    const oneOfMinTypes = getOneOfMinValidateTypes(tool.active_validate);

    if (isOneOfMinActiveValidate(tool.active_validate)
      && !passesOneOfMinValidate(tool.active_validate, counts, inputParams)) {
      const options = tool.active_validate.options as InputType[];
      const connectedOptions = options.filter(
        (type) => (counts[type as InputType] ?? 0) > 0,
      );

      if (!connectedOptions.length) {
        options.forEach((type) => missingTypes.add(type as InputType));
      } else {
        options.forEach((type) => {
          const inputType = type as InputType;
          const minReq = getMinRequirementForType(inputParams, inputType);
          const effectiveMin = minReq > 0 ? minReq : 1;
          if ((counts[inputType] ?? 0) < effectiveMin) {
            missingTypes.add(inputType);
          }
        });
      }
    }

    for (const [, param] of getValidatedInputParameters(inputParams)) {
      const type = param.type as InputType;
      const min = param.min ?? 0;
      if (!oneOfMinTypes?.has(type) && min > 0 && (counts[type] ?? 0) < min) {
        missingTypes.add(type);
      }
    }
  }

  return [...missingTypes];
}

/**
 * 推导 model 缺少的素材类型（规则 3：连少了）。
 */
function getMissingRequiredMediaTypes(
  model: MediaToolsModelConfig,
  counts: InputTypeCounts,
): InputType[] {
  const tools = model.tool_list ?? [];
  if (!tools.length) return [];

  const insufficientTypes = getInsufficientConnectedMediaTypes(tools, counts);
  if (insufficientTypes.length) return insufficientTypes;

  if (shouldShowRequiresImageAndVideoRule(model, counts)) {
    return counts.image > 0 ? ['video'] : ['image'];
  }

  const totalInput = counts.text + counts.image + counts.video + counts.audio;
  if (totalInput === 0) {
    return getEmptyInputRequiredTypes(tools);
  }

  return [];
}

/**
 * 判断 model 是否必须连入图片或视频（纯文字/无连入时均不兼容）。
 */
function modelRequiresMediaInput(model: MediaToolsModelConfig): boolean {
  const emptyCounts: InputTypeCounts = {
    text: 0, image: 0, video: 0, audio: 0,
  };
  const textOnlyCounts: InputTypeCounts = {
    text: 1, image: 0, video: 0, audio: 0,
  };

  return model.tool_list?.every(
    (tool) => !isToolCompatible(tool, emptyCounts) && !isToolCompatible(tool, textOnlyCounts),
  ) ?? false;
}

/**
 * 判断 model 下所有 tool 是否均未声明支持指定连入类型。
 */
function allToolsRejectMediaType(
  tools: MediaToolsModelToolConfig[],
  type: InputType,
): boolean {
  if (!tools.length) return false;
  return tools.every((tool) => {
    const allowedTypes = getAllowedConnectionTypes(tool.input_parameters ?? {});
    return !allowedTypes.has(type);
  });
}

/**
 * 连入素材不被 model 支持或连入过多时，返回规则 2 对应的素材类型。
 */
function getOverConnectedMediaTypes(
  tools: MediaToolsModelToolConfig[],
  counts: InputTypeCounts,
): InputType[] {
  const unsupportedTypes = getUnsupportedConnectedMediaTypes(tools, counts);
  const excessiveTypes = getExcessiveConnectedMediaTypes(tools, counts);
  return [...new Set([...unsupportedTypes, ...excessiveTypes])];
}

/**
 * 推导禁用模型的 hover 提示文案；兼容模型返回 undefined。
 * 优先级：1. 无连入/纯文字不支持 → 2. 连入不支持/过多 → 3. 缺少必需素材。
 */
export function getDisabledModelReason(
  model: MediaToolsModelConfig,
  inputNodeId: FlowNodeData['inputNodeId'],
): string | undefined {
  const counts = deriveInputTypeCounts(inputNodeId);
  if (isModelCompatible(model, inputNodeId)) return undefined;

  const tools = model.tool_list ?? [];

  if (shouldShowTextOnlyDisabledReason(counts) && modelRequiresMediaInput(model)) {
    return getSmartcatTranslationText('do_not_support_text');
  }

  const overConnectedTypes = getOverConnectedMediaTypes(tools, counts);
  if (overConnectedTypes.length) {
    return getDisabledModelReasonMessage('do_not_support_image', overConnectedTypes);
  }

  const missingTypes = getMissingRequiredMediaTypes(model, counts);
  if (missingTypes.length) {
    return getDisabledModelReasonMessage('need_image_and_video', missingTypes);
  }

  return getDisabledModelReasonMessage('need_image_and_video', ['image', 'video']);
}

export interface MatchResult {
  model: MediaToolsModelConfig;
  tool: MediaToolsModelToolConfig;
}

/**
 * 在指定 model 的 tool_list 中，找到与输入兼容的 tool。
 * 若传入 preferredNodeModelId 且该 tool 仍兼容，则优先保留当前 tool。
 */
export function findMatchingToolInModel(
  model: MediaToolsModelConfig | undefined,
  counts: InputTypeCounts,
  preferredNodeModelId?: string,
): MediaToolsModelToolConfig | undefined {
  if (!model?.tool_list?.length) return undefined;

  if (preferredNodeModelId) {
    const preferredTool = model.tool_list.find(
      (tool) => tool.node_model_id === preferredNodeModelId && isToolCompatible(tool, counts),
    );
    if (preferredTool) return preferredTool;
  }

  return model.tool_list.find((tool) => isToolCompatible(tool, counts));
}

export interface AutoMatchOptions {
  /** 指定 model 时仅在该 model 的 tool_list 内匹配，不切换 model */
  fixedModelId?: string;
}

/**
 * 根据连入输入自动匹配 model + tool，返回更新后的节点 data。
 * 未指定 fixedModelId 时，优先沿用节点 mcpInfo 中的当前 model；
 * 仅当当前 model 下无兼容 tool 时，再按配置顺序全局重匹配。
 * 供 autoMatchAndUpdateNode 与手动切换 model 时复用。
 */
export function resolveAutoMatchedNodeData(
  node: FlowNodeEntity,
  data: FlowNodeData,
  models: MediaToolsModelConfig[],
  inputNodeId: FlowNodeData['inputNodeId'],
  options?: AutoMatchOptions,
): FlowNodeData | undefined {
  const counts = deriveInputTypeCounts(inputNodeId);
  let match: MatchResult | undefined;

  if (options?.fixedModelId) {
    const model = models.find((item) => item.model_id === options.fixedModelId);
    const tool = findMatchingToolInModel(model, counts);
    if (model && tool) {
      match = { model, tool };
    }
  } else {
    const currentModelId = data.mcpInfo?.model_id;
    if (currentModelId) {
      const currentModel = models.find((item) => item.model_id === currentModelId);
      const tool = findMatchingToolInModel(
        currentModel,
        counts,
        data.mcpInfo?.node_model_id,
      );
      if (currentModel && tool) {
        match = { model: currentModel, tool };
      }
    }

    if (!match) {
      match = findMatchingModelTool(models, counts);
    }
  }

  if (!match) return undefined;

  const parameters = {
    ...getDefaultParams(match.tool.parameters),
    ...getPreservedRatioParameter(data, models, match),
  };
  const promptKey = getMcpInfoParameterKey(node, 'prompt');
  if (typeof promptKey === 'string') {
    parameters[promptKey] = data.mcpInfo?.parameters?.[promptKey];
  }

  const generationConfig = {
    model_id: match.model.model_id,
    model_name: match.model.model_name,
    api_name: match.tool.api_name,
    node_model_id: match.tool.node_model_id,
    name: match.tool.name,
    parameters,
  };

  return {
    ...data,
    mcpInfo: buildMcpInfoFromGenerationConfig(generationConfig, parameters),
  } as FlowNodeData;
}

/**
 * 媒体节点类型是否支持 input_parameters 自动匹配。
 */
export function isAutoMatchMediaNodeType(nodeType: TVFlowNodeNextTypeEnum): boolean {
  return Boolean(MEDIA_NODE_TYPE_TO_CONFIG_KEY[nodeType]);
}

/**
 * 读取节点类型对应的 modelConfig 列表。
 */
export function getMediaModelsByNodeType(
  nodeType: TVFlowNodeNextTypeEnum,
  modelConfig: ReturnType<typeof useFlowNextStore.getState>['modelConfig'],
): MediaToolsModelConfig[] | undefined {
  const configKey = MEDIA_NODE_TYPE_TO_CONFIG_KEY[nodeType];
  if (!configKey) return undefined;
  const models = modelConfig?.[configKey];
  return Array.isArray(models) && models.length ? models : undefined;
}

/**
 * 按模型顺序 → tool_list 顺序遍历，找到第一个兼容的 model + tool 组合。
 */
export function findMatchingModelTool(
  models: MediaToolsModelConfig[],
  counts: InputTypeCounts,
): MatchResult | undefined {
  for (const model of models) {
    if (!model.tool_list?.length) {
      // eslint-disable-next-line no-continue
      continue;
    }
    for (const tool of model.tool_list) {
      if (isToolCompatible(tool, counts)) {
        return { model, tool };
      }
    }
  }
  return undefined;
}

/**
 * 从工具参数配置里查找指定类型的参数 key。
 */
function findParameterKeyByType(
  parameters: MediaToolsModelToolConfig['parameters'] | undefined,
  type: string,
): string | undefined {
  return Object.entries(parameters ?? {}).find(([, config]) => config?.type === type)?.[0];
}

/**
 * 判断下一个工具的比例配置是否支持旧比例值；未下发选项时继续允许保留旧值。
 */
function isRatioValueSupportedByParameter(
  parameter: MediaToolsModelToolConfig['parameters'][string] | undefined,
  value: unknown,
): boolean {
  if (!parameter?.options?.length) return true;
  return parameter.options.some((option) => String(option.value) === String(value));
}

/**
 * 自动匹配工具时仅保留新工具支持的 ratio 参数；不支持时回退到新工具 default。
 */
function getPreservedRatioParameter(
  data: FlowNodeData,
  models: MediaToolsModelConfig[],
  match: MatchResult,
): Record<string, any> {
  const nextRatioKey = findParameterKeyByType(match.tool.parameters, 'ratio');
  if (!nextRatioKey) return {};

  const nextRatioParameter = match.tool.parameters?.[nextRatioKey];
  const previousParameters = data.mcpInfo?.parameters ?? {};
  if (Object.prototype.hasOwnProperty.call(previousParameters, nextRatioKey)) {
    const previousRatioValue = previousParameters[nextRatioKey];
    if (!isRatioValueSupportedByParameter(nextRatioParameter, previousRatioValue)) {
      return {};
    }
    return {
      [nextRatioKey]: previousRatioValue,
    };
  }

  const previousModel = models.find((item) => item.model_id === data.mcpInfo?.model_id);
  const previousTool = previousModel?.tool_list?.find(
    (tool) => tool.node_model_id === data.mcpInfo?.node_model_id,
  );
  const previousRatioKey = findParameterKeyByType(previousTool?.parameters, 'ratio');
  if (previousRatioKey && Object.prototype.hasOwnProperty.call(previousParameters, previousRatioKey)) {
    const previousRatioValue = previousParameters[previousRatioKey];
    if (!isRatioValueSupportedByParameter(nextRatioParameter, previousRatioValue)) {
      return {};
    }
    return {
      [nextRatioKey]: previousRatioValue,
    };
  }

  return {};
}

/**
 * 连线变化后，自动匹配并更新目标节点的模型和工具。
 * 供 transmitInputParameters 在 ADD_LINE / DELETE_LINE 后调用。
 * 后链路节点、特效模板节点不自动匹配，避免覆盖专用 parameters。
 *
 * @param toNode 目标节点
 * @param nextInputNodeId 更新后的 inputNodeId
 */
export function autoMatchAndUpdateNode(
  toNode: FlowNodeEntity,
  nextInputNodeId: FlowNodeData['inputNodeId'],
): boolean {
  const nodeType = toNode.flowNodeType as TVFlowNodeNextTypeEnum;
  const configKey = MEDIA_NODE_TYPE_TO_CONFIG_KEY[nodeType];
  if (!configKey) return true; // 非媒体节点不处理

  // 后链路 / 特效模板节点不自动匹配，保留各自专用 parameters（如 effect_id）
  const nodeData = (toNode.toJSON()?.data ?? {}) as FlowNodeData;
  if (nodeData.isBacklink || isEffectTemplateNodeData(nodeData)) return true;

  const { modelConfig } = useFlowNextStore.getState();
  const models = getMediaModelsByNodeType(nodeType, modelConfig);
  if (!models?.length) return false;

  const data = (toNode.toJSON()?.data ?? {}) as FlowNodeData;
  const nextData = resolveAutoMatchedNodeData(toNode, data, models, nextInputNodeId);
  if (!nextData) return false;

  const currentModelId = data.mcpInfo?.model_id;
  const currentNodeModelId = data.mcpInfo?.node_model_id;
  const nextModelId = nextData.mcpInfo?.model_id;
  const nextNodeModelId = nextData.mcpInfo?.node_model_id;

  if (nextModelId === currentModelId && nextNodeModelId === currentNodeModelId) {
    return true;
  }

  if (!toNode.form) return false;
  toNode.form.updateFormValues(nextData);
  return true;
}

export { deriveInputTypeCounts };
